/**
 * The send ledger: who was mailed, who was not, and why not.
 *
 * The "why not" half is the point. A campaign whose audience was 4 000 and
 * whose send was 3 100 is not explainable from counters alone, so every
 * candidate that was examined has a row here with a named reason, and the
 * reason chips above the list are the fastest way to ask "show me the 900".
 *
 * Rows are pills, like every other list in the CRM. A recipient IS an object
 * you look at one at a time - the row's subject is a company and its verdict is
 * a status - which is exactly the case the pill row was designed for.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, ListPlus, Loader2, MailOpen, Users } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import {
  enrolCampaign,
  listRecipients,
  SKIP_REASON_ORDER,
  type EnrolOutcome,
  type RecipientRow,
  type SkipReason,
} from "../../../../lib/marketing";
import {
  Btn, EmptyState, SearchField, StatusPill, Toolbar, ToolbarGap, listCard,
} from "../../../ui";
import { Chip, dt, num } from "../shared";
import { recipientTone } from "../status";

const STATUSES = [
  "pending", "sent", "skipped", "failed", "bounced", "complained", "replied", "unsubscribed",
] as const;

/** Company, address, status, reason, when. */
const COLS = "minmax(0,1.5fr) minmax(0,1.6fr) 1fr minmax(0,1.6fr) 1.1fr";

export function RecipientsPanel({
  campaignId,
  skipReasons,
  canManage,
}: {
  campaignId: string;
  skipReasons: Partial<Record<SkipReason, number>>;
  /** Enrolment writes rows, so it needs the same key the campaign form does. */
  canManage: boolean;
}) {
  const t = useT();
  const [rows, setRows] = useState<RecipientRow[] | null>(null);
  const [total, setTotal] = useState(0);
  const [pageCount, setPageCount] = useState(1);

  const [q, setQ] = useState("");
  const [committedQ, setCommittedQ] = useState("");
  const [status, setStatus] = useState<string | null>(null);
  const [reason, setReason] = useState<SkipReason | null>(null);
  const [page, setPage] = useState(1);
  const pageSize = 50;

  // Enrolment: the audience written into this ledger. Two steps on purpose -
  // the first press asks without writing, so the operator reads the numbers
  // before any row exists.
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<EnrolOutcome | null>(null);

  useEffect(() => {
    const id = setTimeout(() => setCommittedQ(q.trim()), 350);
    return () => clearTimeout(id);
  }, [q]);

  useEffect(() => {
    setPage(1);
  }, [committedQ, status, reason]);

  const load = useCallback(async () => {
    setRows(null);
    try {
      const res = await listRecipients(campaignId, {
        q: committedQ || undefined,
        status: status ?? undefined,
        // Filtering by reason only makes sense among the skipped, and asking
        // for both at once would silently return nothing.
        skipReason: reason ?? undefined,
        page,
        pageSize,
      });
      setRows(res.rows);
      setTotal(res.total);
      setPageCount(Math.max(1, res.pageCount));
    } catch {
      setRows([]);
      setTotal(0);
    }
  }, [campaignId, committedQ, status, reason, page]);

  useEffect(() => {
    void load();
  }, [load]);

  const reasonChips = SKIP_REASON_ORDER.map((r) => ({ r, n: skipReasons[r] ?? 0 })).filter((x) => x.n > 0);
  const filtered = committedQ !== "" || status !== null || reason !== null;

  return (
    <div>
      <Toolbar>
        <SearchField value={q} onChange={setQ} placeholder={t("marketing.recipients.searchPlaceholder")} />
        <ToolbarGap />
        <span className="crm-sub px-1.5">{t("marketing.recipients.count", { count: num(total) })}</span>
        {canManage ? (
          <Btn
            small
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                // Ask first. A written ledger is the thing that later decides
                // who gets a letter, so the operator sees the numbers before
                // the rows exist.
                const dry = outcome?.dryRun !== true || outcome.campaignId !== campaignId;
                const res = await enrolCampaign(campaignId, { dryRun: dry });
                setOutcome(res);
                if (!res.dryRun) await load();
              } catch {
                setOutcome(null);
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? <Loader2 className="size-4 animate-spin" /> : <ListPlus className="size-4" />}
            {outcome?.dryRun ? t("marketing.enrol.confirm") : t("marketing.enrol.action")}
          </Btn>
        ) : null}
      </Toolbar>

      {outcome ? <EnrolResult outcome={outcome} t={t} /> : null}

      <div className="mb-3.5 flex flex-wrap items-center gap-1.5 px-1.5">
        {STATUSES.map((s) => (
          <Chip
            key={s}
            active={status === s}
            onClick={() => {
              setStatus(status === s ? null : s);
              // "skipped" is the only status a reason can coexist with.
              if (s !== "skipped") setReason(null);
            }}
          >
            {t(`marketing.recipientStatus.${s}`)}
          </Chip>
        ))}
      </div>

      {reasonChips.length > 0 ? (
        <div className="mb-3.5 flex flex-wrap items-center gap-1.5 border-t border-[var(--crm-hair)] px-1.5 pt-3">
          <span className="crm-sub mr-1 text-[11.5px]">{t("marketing.recipients.byReason")}</span>
          {reasonChips.map(({ r, n }) => (
            <Chip
              key={r}
              active={reason === r}
              count={num(n)}
              onClick={() => {
                const next = reason === r ? null : r;
                setReason(next);
                if (next) setStatus("skipped");
              }}
            >
              {t(`marketing.skip.${r}`)}
            </Chip>
          ))}
        </div>
      ) : null}

      <div className="crm-card" style={listCard(COLS)}>
        {rows === null ? (
          <div className="flex justify-center py-16 text-muted-foreground">
            <Loader2 className="size-6 animate-spin" />
          </div>
        ) : rows.length === 0 ? (
          // A filter that matched nothing and a campaign that has never run are
          // different facts, and only one of them is about this campaign.
          <EmptyState
            icon={Users}
            title={t(filtered ? "marketing.recipients.noMatch" : "marketing.recipients.empty")}
            body={t(filtered ? "marketing.recipients.noMatchBody" : "marketing.recipients.emptyBody")}
          />
        ) : (
          <>
            <div className="crm-list-head" style={{ gridTemplateColumns: COLS, paddingTop: 22 }}>
              <span>{t("marketing.recipients.col.company")}</span>
              <span>{t("marketing.recipients.col.email")}</span>
              <span>{t("marketing.recipients.col.status")}</span>
              <span>{t("marketing.recipients.col.reason")}</span>
              <span>{t("marketing.recipients.col.when")}</span>
            </div>

            <div className="crm-rows pt-0">
              {rows.map((r) => (
                <div key={r.id} className="crm-list-row" style={{ gridTemplateColumns: COLS }}>
                  <span className="crm-prow-n crm-trunc pl-3">
                    {r.companyName ?? r.regCode ?? r.domain ?? "-"}
                  </span>
                  <span className="crm-mut crm-tnum crm-trunc text-[12.5px]">{r.email}</span>
                  <span>
                    <StatusPill tone={recipientTone(r.status)}>
                      {t(`marketing.recipientStatus.${r.status}`)}
                    </StatusPill>
                  </span>
                  <span className="crm-mut crm-trunc text-[12.5px]">
                    {r.skipReason ? t(`marketing.skip.${r.skipReason}`) : (r.error ?? "-")}
                    {/* Which earlier recipient or suppression entry caused it.
                        Without this, "duplicate" is a claim rather than a fact. */}
                    {r.skippedFor ? <span className="crm-tnum ml-1 text-[11px] opacity-70">{r.skippedFor}</span> : null}
                  </span>
                  <span className="crm-mut flex min-w-0 items-center gap-2 text-[12.5px]">
                    <span className="crm-tnum crm-trunc">{dt(r.sentAt ?? r.queuedAt)}</span>
                    {/* The message we sent is a real thread in the shared
                        inbox, so the ledger row is a door into it rather than
                        a dead end. Mirrors the ticket link on a mail thread. */}
                    {r.threadId ? (
                      <Link
                        to={`/email/${r.threadId}`}
                        className="shrink-0 text-[var(--color-primary)] hover:underline"
                        title={t("marketing.recipients.openThread")}
                        aria-label={t("marketing.recipients.openThread")}
                      >
                        <MailOpen className="size-3.5" />
                      </Link>
                    ) : null}
                  </span>
                </div>
              ))}
            </div>

            {pageCount > 1 ? (
              <div className="crm-tfoot">
                {t("marketing.recipients.page", { page, pageCount })}
                <span className="crm-push flex gap-2.5">
                  <Btn small disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                    <ChevronLeft className="size-4" />
                    {t("common.prev")}
                  </Btn>
                  <Btn small disabled={page >= pageCount} onClick={() => setPage((p) => Math.min(pageCount, p + 1))}>
                    {t("common.next")}
                    <ChevronRight className="size-4" />
                  </Btn>
                </span>
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * What an enrolment run did, or would do.
 *
 * Every number here exists because the difference between them is where a
 * campaign becomes unexplainable. "Examined 300, wrote 277" needs the other 23
 * accounted for, and two of the reasons are not failures at all: a mailbox that
 * several companies share is written once, and a row held back by a cooldown or
 * a run cap is left for the next run rather than frozen into the ledger as
 * skipped forever.
 */
function EnrolResult({
  outcome,
  t,
}: {
  outcome: EnrolOutcome;
  t: (key: string, vars?: Record<string, string | number>) => string;
}) {
  const deferred = Object.entries(outcome.deferred).filter(([, n]) => n > 0);
  const unstorable = Object.entries(outcome.unstorable).filter(([, n]) => n > 0);

  return (
    <div className="mb-3.5 rounded-xl border border-[var(--crm-hair)] bg-[var(--crm-soft)] px-3 py-2.5 text-[12.5px]">
      <div className="font-[650]">
        {outcome.dryRun ? t("marketing.enrol.dryTitle") : t("marketing.enrol.doneTitle")}
      </div>
      <ul className="mt-1 space-y-0.5 text-[var(--crm-muted)]">
        <li>{t("marketing.enrol.examined", { examined: num(outcome.examined), total: num(outcome.total) })}</li>
        {outcome.dryRun ? null : (
          <li>
            {t("marketing.enrol.written", {
              pending: num(outcome.created.pending),
              skipped: num(outcome.created.skipped),
            })}
          </li>
        )}
        {!outcome.dryRun && outcome.alreadyThere > 0 ? (
          <li>{t("marketing.enrol.alreadyThere", { n: num(outcome.alreadyThere) })}</li>
        ) : null}
        {outcome.collapsed > 0 ? (
          <li>{t("marketing.enrol.collapsed", { n: num(outcome.collapsed) })}</li>
        ) : null}
        {deferred.length ? (
          <li>
            {t("marketing.enrol.deferred", {
              list: deferred.map(([r, n]) => `${t(`marketing.skip.${r}`)} ${num(n)}`).join(", "),
            })}
          </li>
        ) : null}
        {unstorable.length ? (
          <li>
            {t("marketing.enrol.unstorable", {
              list: unstorable.map(([r, n]) => `${t(`marketing.skip.${r}`)} ${num(n)}`).join(", "),
            })}
          </li>
        ) : null}
        {outcome.partial ? <li>{t("marketing.enrol.partial")}</li> : null}
      </ul>
    </div>
  );
}
